import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Button, Divider } from "@mui/material";

/* ================= SEQUENCE DISPLAY ================= */
function SeqBox({ children }) {
  return (
    <Box
      sx={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        fontFamily: "monospace",
        fontSize: 13,
        bgcolor: "#f5f5f5",
        color: "#111",
        p: 2,
        borderRadius: 2,
        mt: 1,
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Box>
  );
}

function CenteredImage({ src, alt }) {
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: "#fafafa",
        borderRadius: 2,
        textAlign: "center",
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: "100%", borderRadius: 8 }}
        onLoad={() => console.log("✅ Loaded:", src)}
        onError={(e) => console.error("❌ Image failed:", e.target.src)}
      />
    </Box>
  );
}

/* ================= MAIN PAGE ================= */
export default function NucleoReadResults() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state) {
    return (
      <Typography sx={{ p: 4 }} color="error">
        No analysis data found. Please run NucleoRead again.
      </Typography>
    );
  }

  const { job_id, result, images } = state;

  console.log("🧬 Job:", job_id);
  console.log("📊 Result:", result);
  console.log("🖼️ Images:", images);

  const bestOrf = result.best_orf || null;

  return (
    <Paper sx={{ maxWidth: 1100, mx: "auto", p: 4, mt: 4 }}>
      <Typography variant="h4" fontWeight={700} textAlign="center" mb={3}>
        🧬 NucleoRead Analysis Report
      </Typography>

      {/* ================= CORE METRICS ================= */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6">{result.length}</Typography>
          <Typography variant="body2">Bases</Typography>
        </Paper>

        <Paper sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6">{result.gc}%</Typography>
          <Typography variant="body2">GC Content</Typography>
        </Paper>

        <Paper sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6">{result.at}%</Typography>
          <Typography variant="body2">AT/AU Content</Typography>
        </Paper>

        <Paper sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6">{result.ambiguous}</Typography>
          <Typography variant="body2">Ambiguous Bases</Typography>
        </Paper>
      </Box>

      {/* ================= SEQUENCES ================= */}
      <Typography variant="h6">🧪 RNA Transcription</Typography>
      <SeqBox>{result.rna}</SeqBox>

      <Typography mt={2} variant="h6">
        🔁 Complement
      </Typography>
      <SeqBox>{result.complement}</SeqBox>

      <Typography mt={2} variant="h6">
        🔁 Reverse Complement
      </Typography>
      <SeqBox>{result.reverse_complement}</SeqBox>

      {/* ================= ORF ================= */}
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">🧬 Longest ORF</Typography>
      {bestOrf ? (
        <>
          <Typography>
            Frame: {bestOrf.frame} ({bestOrf.strand})
          </Typography>
          <Typography>Start: {bestOrf.start}</Typography>
          <Typography>End: {bestOrf.end}</Typography>
          <Typography>Length: {bestOrf.length} bp</Typography>
        </>
      ) : (
        <Typography color="text.secondary">
          No ORF detected for this sequence.
        </Typography>
      )}

      <Typography mt={2} variant="h6">
        🧫 Translated Protein
      </Typography>
      <SeqBox>{result.protein}</SeqBox>

      {/* ================= PCR PRIMERS ================= */}
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">🧪 PCR Primer Design</Typography>

      {result.primers ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mt: 1,
          }}
        >
          {/* Forward Primer */}
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="subtitle2">Forward Primer</Typography>
            <SeqBox>{result.primers.fwd}</SeqBox>
            <Typography variant="body2">
              Tm: {result.primers.fwd_tm}°C
            </Typography>
          </Paper>

          {/* Reverse Primer */}
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="subtitle2">Reverse Primer</Typography>
            <SeqBox>{result.primers.rev}</SeqBox>
            <Typography variant="body2">
              Tm: {result.primers.rev_tm}°C
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Typography mt={1} color="text.secondary">
          No primers designed (sequence too short or invalid).
        </Typography>
      )}

      {/* ================= RESTRICTION SITES ================= */}
      {Array.isArray(result.restriction_sites) &&
        result.restriction_sites.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6">✂️ Restriction Mapping</Typography>
            {result.restriction_sites.map((site) => (
              <Box key={site.name} sx={{ mt: 1 }}>
                <Typography variant="subtitle2">
                  {site.name} ({site.site})
                </Typography>
                <Typography variant="body2">
                  Positions: {site.positions.join(", ")}
                </Typography>
              </Box>
            ))}
          </>
        )}

      {/* ================= IMAGES ================= */}
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">📊 GC Content Plot</Typography>
      <CenteredImage src={images.gc} alt="GC Plot" />

      <Typography mt={3} variant="h6">
        🧪 Virtual Gel
      </Typography>
      <CenteredImage src={images.gel} alt="Virtual Gel" />

      <Divider sx={{ my: 3 }} />

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={() => navigate("/seqintel/nucleoread")}
      >
        🔁 Run Another Sequence
      </Button>
    </Paper>
  );
}
